import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronRight,
  CircleSlash,
  Copy,
  FolderTree,
  Link2Off,
  RotateCcw,
  Tags,
  UserRoundX,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton, useToastStore } from '@/shared/ui';

import type { ImportConflictGroup, ImportConflictItem, ImportConflictKind } from '../model/types';
import {
  useImportConflicts,
  useMapType,
  useRequestAnalysis,
  useResolveBulk,
  useResolveConflict,
} from '../queries/useImport';
import { ConflictDetail } from './ConflictDetail';
import styles from './Import.module.css';

/**
 * ⭐ <b>Akcja hurtowa stoi przy NAGŁÓWKU GRUPY, nie nad całą listą</b> — i dwie grupy świadomie
 * jej nie mają. „Wszędzie wartość z pliku" ma sens dla różnic wartości, a jest groźne dla
 * przeprowadzek: jedno kliknięcie przemeblowałoby dziesiątki poddrzew. Lepiej powiedzieć wprost
 * „tu nie ma skrótu", niż mieć przycisk i liczyć, że nikt go nie naciśnie.
 */
const BULK: Partial<Record<ImportConflictKind, string>> = {
  duplicateSuspect: 'create_separate',
  valueDiffers: 'take_file',
  // ⚠ Konta ZABLOKOWANE dostają skrót „wszędzie bez właściciela": po odejściu człowieka to
  //    najczęstsza odpowiedź, a wskazanie następcy i tak wymaga wyboru per adres.
  disabledOwner: 'leave_no_owner',

  // ⚠ Konta CZEKAJĄCE dostają skrót „przypisz mimo to" — one po prostu jeszcze nie weszły.
  inactiveOwner: 'assign',
  danglingRef: 'skip',
};

const ICONS: Record<ImportConflictKind, LucideIcon> = {
  duplicateSuspect: Copy,
  reparent: FolderTree,
  deletedReturns: RotateCcw,
  valueDiffers: Tags,
  disabledOwner: UserRoundX,
  inactiveOwner: UserRoundX,
  unknownType: Tags,
  danglingRef: Link2Off,
};

interface Props {
  runId: number;
  groups: readonly ImportConflictGroup[];
  readOnly: boolean;
}

export function ConflictGroups({ runId, groups, readOnly }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<ImportConflictKind | null>(groups[0]?.kind ?? null);

  if (groups.length === 0) {
    return (
      <output className={styles['clear']}>
        <span className={styles['clearTitle']}>{t('import.conflicts.none')}</span>
        <span className={styles['clearHint']}>{t('import.conflicts.noneHint')}</span>
      </output>
    );
  }

  return (
    <div className={styles['groups']}>
      {groups.map((group) => (
        <ConflictGroupSection
          key={group.kind}
          runId={runId}
          group={group}
          readOnly={readOnly}
          expanded={open === group.kind}
          onToggle={() => setOpen(open === group.kind ? null : group.kind)}
        />
      ))}
    </div>
  );
}

interface SectionProps {
  runId: number;
  group: ImportConflictGroup;
  readOnly: boolean;
  expanded: boolean;
  onToggle: () => void;
}

function ConflictGroupSection({ runId, group, readOnly, expanded, onToggle }: SectionProps) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const bulk = useResolveBulk(runId);

  const czeka = group.total - group.resolved - group.skipped;
  const bulkChoice = BULK[group.kind];
  const Icon = ICONS[group.kind];
  const regionId = `import-group-${group.kind}`;

  return (
    <section className={styles['group']} data-open={expanded ? 'true' : undefined}>
      <h3 className={styles['groupHead']}>
        <button
          type="button"
          className={styles['groupToggle']}
          aria-expanded={expanded}
          aria-controls={regionId}
          onClick={onToggle}
        >
          <ChevronRight className={styles['chevron']} size={16} aria-hidden="true" />
          <Icon className={styles['groupIcon']} size={16} aria-hidden="true" />
          <span className={styles['groupName']}>{t(`import.kind.${group.kind}`)}</span>
          <span className={styles['groupCount']}>
            {czeka > 0
              ? t('import.conflicts.groupCount', { waiting: czeka, total: group.total })
              : t('import.conflicts.groupDone', { total: group.total })}
          </span>
        </button>

        {!readOnly && czeka > 0 ? (
          bulkChoice ? (
            <Button
              variant="ghost"
              disabled={bulk.isPending}
              onClick={() => {
                bulk.mutate(
                  { kind: group.kind, choice: bulkChoice },
                  {
                    onSuccess: (result) =>
                      show({
                        tone: 'success',
                        message: t('import.conflicts.bulkDone', { count: result.affected }),
                      }),
                    onError: (error) => show({ tone: 'danger', message: describeError(error) }),
                  },
                );
              }}
            >
              {t(`import.bulk.${group.kind}`)}
            </Button>
          ) : (
            // ⛔ Mówimy wprost, że skrótu nie ma. Brak przycisku bez wyjaśnienia wygląda
            // jak niedokończony ekran, a nie jak decyzja.
            <span className={styles['noBulk']}>
              <CircleSlash size={13} aria-hidden="true" />
              {t('import.conflicts.noBulk')}
            </span>
          )
        ) : null}
      </h3>

      <div id={regionId} hidden={!expanded}>
        {expanded ? <ConflictList runId={runId} kind={group.kind} readOnly={readOnly} /> : null}
      </div>
    </section>
  );
}

interface ListProps {
  runId: number;
  kind: ImportConflictKind;
  readOnly: boolean;
}

const PAGE = 25;

function ConflictList({ runId, kind, readOnly }: ListProps) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);

  const page = useImportConflicts(runId, { kind, offset, limit: PAGE });

  if (page.isPending) {
    return (
      <div className={styles['rows']} aria-busy="true">
        <Skeleton height="3rem" announce />
        <Skeleton height="3rem" />
      </div>
    );
  }

  if (page.isError) {
    return (
      <p className={styles['rowsError']} role="alert">
        {describeError(page.error)}
      </p>
    );
  }

  const { items, total } = page.data;

  return (
    <>
      <ul className={styles['rows']}>
        {items.map((item) => (
          <ConflictRow
            key={item.id}
            runId={runId}
            item={item}
            readOnly={readOnly}
            expanded={openId === item.id}
            onToggle={() => setOpenId(openId === item.id ? null : item.id)}
            onResolved={() => {
              // ⭐ Po rozstrzygnięciu otwiera się NASTĘPNY nierozstrzygnięty wiersz — ale
              // przejście ZATRZYMUJE SIĘ NA GRANICY GRUPY (potwierdzone 2026-08-08). Wejście
              // w inny rodzaj konfliktu bez zauważenia jest sposobem na klikanie decyzji,
              // których się nie rozumie.
              const kolejny = items.find((row) => row.id !== item.id && row.state === 'waiting');
              setOpenId(kolejny?.id ?? null);
            }}
          />
        ))}
      </ul>

      {total > PAGE ? (
        <div className={styles['pager']}>
          <Button
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            {t('import.conflicts.prev')}
          </Button>
          <span className={styles['pagerInfo']}>
            {t('import.conflicts.range', {
              from: offset + 1,
              to: Math.min(offset + PAGE, total),
              total,
            })}
          </span>
          <Button
            variant="ghost"
            disabled={offset + PAGE >= total}
            onClick={() => setOffset(offset + PAGE)}
          >
            {t('import.conflicts.next')}
          </Button>
        </div>
      ) : null}
    </>
  );
}

interface RowProps {
  runId: number;
  item: ImportConflictItem;
  readOnly: boolean;
  expanded: boolean;
  onToggle: () => void;
  onResolved: () => void;
}

function ConflictRow({ runId, item, readOnly, expanded, onToggle, onResolved }: RowProps) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const resolve = useResolveConflict(runId);
  const mapType = useMapType(runId);
  const analyze = useRequestAnalysis(runId);
  const regionRef = useRef<HTMLDivElement>(null);
  const regionId = `import-conflict-${item.id}`;

  useEffect(() => {
    // Przeniesienie uwagi do rozwiniętego wiersza — inaczej klawiatura zostaje przy nagłówku,
    // a decyzje są w środku.
    if (expanded) regionRef.current?.focus();
  }, [expanded]);

  return (
    <li className={styles['row']} data-state={item.state}>
      <button
        type="button"
        className={styles['rowHead']}
        aria-expanded={expanded}
        aria-controls={regionId}
        onClick={onToggle}
      >
        <ChevronRight className={styles['chevron']} size={15} aria-hidden="true" />

        <span className={styles['rowMain']}>
          <span className={styles['rowTitle']}>{item.fileTitle ?? item.dbTitle ?? item.externalKey}</span>
          <span className={styles['rowKey']}>{item.externalKey}</span>
        </span>

        <span className={styles['badges']}>
          {/* ⭐ Plakietka rodzaju niesie też jego WŁASNY stan — pozycja z dwoma rodzajami
              zostaje „czeka" po rozstrzygnięciu jednego z nich, więc bez tego nie widać,
              co jeszcze blokuje. Kształt (ptaszek), nie sam odcień — decyzja 58. */}
          {item.kinds.map((detail) => (
            <span
              key={detail.kind}
              className={styles['badge']}
              data-decided={detail.choice ? 'true' : undefined}
            >
              {detail.choice ? <Check size={11} aria-hidden="true" /> : null}
              {t(`import.kind.${detail.kind}`)}
            </span>
          ))}
        </span>

        <span className={styles['rowState']} data-state={item.state}>
          {t(`import.state.${item.state}`)}
        </span>
      </button>

      <div
        id={regionId}
        ref={regionRef}
        tabIndex={-1}
        className={styles['rowBody']}
        hidden={!expanded}
      >
        {/* ⭐ Powód powrotu pozycji do „czeka" — bez niego wygląda to jak utracona praca. */}
        {expanded && item.staleReasonKey ? (
          <output className={styles['stale']}>
            <RotateCcw size={14} aria-hidden="true" />
            {t(`import.stale.${item.staleReasonKey}`, {
              defaultValue: t('import.stale.file_changed'),
            })}
          </output>
        ) : null}

        {expanded
          ? item.kinds.map((detail) => (
              <ConflictDetail
                key={detail.kind}
                item={item}
                detail={detail}
                readOnly={readOnly}
                busy={resolve.isPending || mapType.isPending}
                onMapType={(targetType) =>
                  mapType.mutate(
                    { fileType: detail.unknownValue?.fileValue ?? '', targetType },
                    {
                      onSuccess: (count) => {
                        // ⭐ Mapowanie jest zapisem STANU przebiegu, nie decyzją o wierszu —
                        // konflikt znika dopiero po ponownej analizie. Zlecamy ją od razu,
                        // bo inaczej ekran wyglądałby, jakby kliknięcie nic nie zrobiło.
                        analyze.mutate();
                        show({
                          tone: 'success',
                          message: t('import.detail.mapped', { count }),
                        });
                      },
                      onError: (error) => show({ tone: 'danger', message: describeError(error) }),
                    },
                  )
                }
                onChoose={(choice) =>
                  resolve.mutate(
                    { conflictId: item.id, kind: detail.kind, choice },
                    {
                      onSuccess: (result) => {
                        if (result.affected > 1) {
                          // ⭐ Pominięcie NOWEGO elementu kaskaduje na całe jego poddrzewo (R2).
                          // Nikt się nie spodziewa, że jedno kliknięcie zdejmuje czterdzieści
                          // siedem bytów — więc mówimy to wprost.
                          show({
                            message: t('import.conflicts.cascaded', { count: result.affected }),
                          });
                        }

                        onResolved();
                      },
                      onError: (error) => show({ tone: 'danger', message: describeError(error) }),
                    },
                  )
                }
              />
            ))
          : null}
      </div>
    </li>
  );
}
